/**
 * Overlapping activities on one shift, and the one-click way out (owner, 2026-10-18): the later
 * activity starts when the earlier one ends. Past the shift's end it is shortened to the end
 * instead, as long as a quarter hour is left; otherwise nothing is proposed and the manager edits
 * by hand. Only a timed task is moved: a planned lunch is the manager's own decision, and a
 * suggested lunch is no activity at all, so it never takes part in a clash.
 */
const MIN = 60_000;
const QUARTER = 15 * MIN;

export interface Slot {
  key: string;
  kind: 'task' | 'lunch';
  id?: number;
  name: string;
  /** start and end, as instants */
  a: number;
  b: number;
  /** a suggested lunch, not planned */
  sugg?: boolean;
}
export type Fix =
  | { kind: 'move' | 'shorten'; id: number; name: string; a: number; b: number }
  | { kind: 'none'; why: 'lunch' | 'no-room' };
export interface Clash {
  first: Slot;
  later: Slot;
  /** the overlap, as instants */
  from: number;
  to: number;
  fix: Fix;
}

/** What one click does about `later` sitting on `first`, inside a shift that ends at `end`. */
export function fixFor(first: Slot, later: Slot, end: number): Fix {
  if (later.kind !== 'task' || later.id == null) return { kind: 'none', why: 'lunch' };
  const a = first.b;
  const len = later.b - later.a;
  if (a + len <= end) return { kind: 'move', id: later.id, name: later.name, a, b: a + len };
  if (end - a >= QUARTER) return { kind: 'shorten', id: later.id, name: later.name, a, b: end };
  return { kind: 'none', why: 'no-room' };
}

/**
 * Every overlapping pair of planned activities, earliest first, each with its fix. A suggested
 * lunch is left out: it is a proposal, not a clash.
 */
export function clashesOf(slots: Slot[], end: number): Clash[] {
  const planned = slots.filter((x) => !x.sugg).sort((p, q) => p.a - q.a || q.b - p.b);
  const out: Clash[] = [];
  planned.forEach((first, i) => {
    for (const later of planned.slice(i + 1))
      if (later.a < first.b)
        out.push({
          first,
          later,
          from: later.a,
          to: Math.min(first.b, later.b),
          fix: fixFor(first, later, end),
        });
  });
  return out;
}
